import React from 'react';
import type {TFunction} from 'i18next';
import {
    scoreCommandSearchFields,
    type CommandSearchContext,
    type CommandSearchProvider,
    type CommandSearchResult,
} from '../../components/panel-command-search';
import {BizLearnVideos} from '../../components/panel-sections/app-bar/biz-learn-videos-action';
import type {PanelAccessServices} from '../types';

export interface TutorialsProviderServices extends PanelAccessServices {
    /** `useTranslation('components', {keyPrefix: 'panel-sections.app-bar.actions.biz-learn-videos'})` */
    bizLearnT: TFunction<'components'>;
}

const getServices = (ctx: CommandSearchContext): TutorialsProviderServices | undefined =>
    ctx.services as TutorialsProviderServices | undefined;

const TUTORIALS_RESULT_LIMIT = 20;

/** Command-search provider surfacing BizLearn tutorial videos. */
export const tutorialsProvider: CommandSearchProvider = {
    id: 'tutorials',
    category: 'tutorials',
    search: (ctx) => {
        const services = getServices(ctx);
        if (!services?.bizLearnT) {
            return [];
        }

        const {bizLearnT} = services;

        const results: CommandSearchResult[] = Object.entries(BizLearnVideos).map(([key, buildTutorial]) => {
            const tutorial = buildTutorial(bizLearnT);
            return {
                id: `tutorial-${key}`,
                category: 'tutorials',
                title: tutorial.title,
                avatarUrl: tutorial.imageUrl,
                score: scoreCommandSearchFields(ctx.query, {
                    title: tutorial.title,
                    category: 'tutorials',
                    type: 'Tutorial',
                }),
                providerId: 'tutorials',
                to: tutorial.url,
                target: '_blank',
                rel: 'noopener noreferrer',
                recentMeta: {actionKey: 'tutorial', actionPayload: {href: tutorial.url}},
                // Primary / keyboard open; href still enables Cmd/Ctrl-click and “Open in new tab”.
                onSelect: () => {
                    window.open(tutorial.url, '_blank', 'noopener,noreferrer');
                },
            };
        });

        return results
            .filter((result) => (result.score ?? 0) > 0)
            .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
            .slice(0, TUTORIALS_RESULT_LIMIT);
    },
    enabled: (ctx) => Boolean(getServices(ctx)?.canAccess?.('base.connectedAppsBizLearn')),
};

export default tutorialsProvider;
